// src/hooks/useSyncedPlayback.ts
import { useState, useRef, useCallback, useEffect } from 'react';
import * as Tone from 'tone';
import { AlphaTabApi } from '@coderline/alphatab';

// Constants
/** Interval in milliseconds for position tracking updates */
const POSITION_UPDATE_INTERVAL_MS = 50;
/** Buffer in seconds to prevent seeking past the end of audio */
const AUDIO_END_BUFFER_SECONDS = 0.1;

/**
 * Options for the synced playback hook
 */
export interface UseSyncedPlaybackOptions {
  /** AlphaTab API instance */
  alphaTabApi: AlphaTabApi | null;
  /** Audio file URL or object URL */
  audioUrl: string | null;
  /** Initial offset in milliseconds (default 0) */
  initialOffset?: number;
}

/**
 * Return type for the synced playback hook
 */
export interface UseSyncedPlaybackReturn {
  /** Whether both audio and AlphaTab are ready */
  isReady: boolean;
  /** Whether currently playing */
  isPlaying: boolean;
  /** Current offset in milliseconds */
  offset: number;
  /** Nudge offset by delta milliseconds */
  nudgeOffset: (deltaMs: number) => void;
  /** Reset offset to 0 */
  resetOffset: () => void;
  /** Set offset to specific value */
  setOffset: (ms: number) => void;
  /** Start synced playback */
  play: () => Promise<void>;
  /** Pause synced playback */
  pause: () => void;
  /** Stop and reset to beginning */
  stop: () => void;
  /** Audio duration in seconds */
  audioDuration: number;
  /** Current audio position in seconds */
  audioPosition: number;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
}

/**
 * Hook to coordinate AlphaTab cursor playback with Tone.js audio playback,
 * allowing for offset adjustment.
 *
 * Offset semantics:
 * - Positive offset = audio starts LATER than cursor (delay audio start)
 * - Negative offset = audio starts EARLIER (skip into audio)
 *
 * @example
 * ```tsx
 * const {
 *   isReady,
 *   isPlaying,
 *   offset,
 *   nudgeOffset,
 *   play,
 *   pause,
 *   stop,
 * } = useSyncedPlayback({
 *   alphaTabApi: apiRef.current,
 *   audioUrl: '/audio/recording.mp3',
 *   initialOffset: 0,
 * });
 * ```
 */
export function useSyncedPlayback({
  alphaTabApi,
  audioUrl,
  initialOffset = 0,
}: UseSyncedPlaybackOptions): UseSyncedPlaybackReturn {
  // State
  const [audioReady, setAudioReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [offset, setOffsetState] = useState(initialOffset);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPosition, setAudioPosition] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const playerRef = useRef<Tone.Player | null>(null);
  const positionIntervalRef = useRef<number | null>(null);
  const playStartTimeRef = useRef<number>(0);
  const audioStartPositionRef = useRef<number>(0);
  const delayedStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Track offset in ref for callbacks
  const offsetRef = useRef(offset);
  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  // Update offset state when initialOffset prop changes
  // (useState only uses initial value on first render)
  useEffect(() => {
    setOffsetState(initialOffset);
  }, [initialOffset]);

  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Computed ready state: both AlphaTab and audio must be ready
  const isReady = Boolean(alphaTabApi && audioReady);

  // Clear position tracking interval
  const clearPositionTracking = useCallback(() => {
    if (positionIntervalRef.current) {
      clearInterval(positionIntervalRef.current);
      positionIntervalRef.current = null;
    }
  }, []);

  // Clear delayed start timeout
  const clearDelayedStart = useCallback(() => {
    if (delayedStartTimeoutRef.current) {
      clearTimeout(delayedStartTimeoutRef.current);
      delayedStartTimeoutRef.current = null;
    }
  }, []);

  // Load audio when URL changes
  useEffect(() => {
    // Reset state
    setAudioReady(false);
    setAudioDuration(0);
    setAudioPosition(0);
    setIsPlaying(false);
    setError(null);
    clearPositionTracking();
    clearDelayedStart();

    if (!audioUrl) {
      return;
    }

    // Cleanup previous player
    if (playerRef.current) {
      playerRef.current.stop();
      playerRef.current.dispose();
      playerRef.current = null;
    }

    setLoading(true);

    const player = new Tone.Player({
      url: audioUrl,
      onload: () => {
        if (!mountedRef.current) return;
        setAudioDuration(player.buffer.duration);
        setAudioReady(true);
        setLoading(false);
      },
      onerror: (err) => {
        if (!mountedRef.current) return;
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(`Failed to load audio: ${errorMessage}`);
        setLoading(false);
      },
    }).toDestination();

    playerRef.current = player;

    return () => {
      player.stop();
      player.dispose();
      playerRef.current = null;
      clearPositionTracking();
      clearDelayedStart();
    };
  }, [audioUrl, clearPositionTracking, clearDelayedStart]);

  // Start position tracking
  const startPositionTracking = useCallback((startPosition: number) => {
    clearPositionTracking();

    playStartTimeRef.current = Tone.now();
    audioStartPositionRef.current = startPosition;

    positionIntervalRef.current = window.setInterval(() => {
      const elapsed = Tone.now() - playStartTimeRef.current;
      const currentPosition = audioStartPositionRef.current + elapsed;

      // Check if we've reached the end
      if (currentPosition >= audioDuration) {
        setAudioPosition(audioDuration);
        // Don't auto-stop here - let playback finish naturally
      } else {
        setAudioPosition(currentPosition);
      }
    }, POSITION_UPDATE_INTERVAL_MS);
  }, [audioDuration, clearPositionTracking]);

  // Play synced playback
  const play = useCallback(async () => {
    if (!alphaTabApi || !playerRef.current || !audioReady) {
      return;
    }

    // Ensure Tone.js audio context is started (required by browsers)
    await Tone.start();

    const currentOffset = offsetRef.current;

    // Start AlphaTab playback immediately
    alphaTabApi.play();

    // Handle audio timing based on offset
    if (currentOffset >= 0) {
      // Positive offset: audio starts AFTER cursor
      // Schedule audio to start after offset milliseconds
      if (currentOffset === 0) {
        // No delay - start immediately
        playerRef.current.start(undefined, 0);
        startPositionTracking(0);
      } else {
        // Delay audio start
        delayedStartTimeoutRef.current = setTimeout(() => {
          if (playerRef.current) {
            playerRef.current.start(undefined, 0);
            startPositionTracking(0);
          }
        }, currentOffset);
        // Start position tracking at 0 but it won't move until audio starts
        setAudioPosition(0);
      }
    } else {
      // Negative offset: audio starts BEFORE cursor (skip into audio)
      const skipSeconds = Math.abs(currentOffset) / 1000;

      // Make sure we don't skip past the end
      const clampedSkip = Math.min(skipSeconds, audioDuration - AUDIO_END_BUFFER_SECONDS);

      playerRef.current.start(undefined, clampedSkip);
      startPositionTracking(clampedSkip);
    }

    setIsPlaying(true);
  }, [alphaTabApi, audioReady, audioDuration, startPositionTracking]);

  // Pause synced playback
  const pause = useCallback(() => {
    clearDelayedStart();
    clearPositionTracking();

    if (alphaTabApi) {
      alphaTabApi.pause();
    }

    if (playerRef.current) {
      playerRef.current.stop();
    }

    setIsPlaying(false);
  }, [alphaTabApi, clearDelayedStart, clearPositionTracking]);

  // Stop and reset
  const stop = useCallback(() => {
    clearDelayedStart();
    clearPositionTracking();

    if (alphaTabApi) {
      alphaTabApi.stop();
    }

    if (playerRef.current) {
      playerRef.current.stop();
    }

    setIsPlaying(false);
    setAudioPosition(0);
  }, [alphaTabApi, clearDelayedStart, clearPositionTracking]);

  // Nudge offset by delta milliseconds
  const nudgeOffset = useCallback((deltaMs: number) => {
    setOffsetState(prev => {
      const newOffset = prev + deltaMs;

      // If currently playing, need to restart with new offset
      if (isPlaying && alphaTabApi && playerRef.current) {
        // Stop current playback
        clearDelayedStart();
        playerRef.current.stop();

        // Restart audio with new offset
        if (newOffset >= 0) {
          // Audio should be delayed - but we're mid-playback
          // For simplicity, just restart from current position with new offset logic
          // This is a simplification - a more sophisticated approach would track
          // cursor position and calculate exact audio position
          playerRef.current.start(undefined, 0);
          startPositionTracking(0);
        } else {
          const skipSeconds = Math.abs(newOffset) / 1000;
          const clampedSkip = Math.min(skipSeconds, audioDuration - AUDIO_END_BUFFER_SECONDS);
          playerRef.current.start(undefined, clampedSkip);
          startPositionTracking(clampedSkip);
        }
      }

      return newOffset;
    });
  }, [isPlaying, alphaTabApi, audioDuration, clearDelayedStart, startPositionTracking]);

  // Reset offset to 0
  const resetOffset = useCallback(() => {
    setOffsetState(0);
  }, []);

  // Set offset to specific value
  const setOffset = useCallback((ms: number) => {
    setOffsetState(ms);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPositionTracking();
      clearDelayedStart();
    };
  }, [clearPositionTracking, clearDelayedStart]);

  return {
    isReady,
    isPlaying,
    offset,
    nudgeOffset,
    resetOffset,
    setOffset,
    play,
    pause,
    stop,
    audioDuration,
    audioPosition,
    loading,
    error,
  };
}
